import React, { useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { connectAttemptFinished } from 'Redux/thunks/subscriptions.thunks';

import { PollToConnectContainer } from '../containers/PollToConnect.container';
import { ConnectMessages } from '../components/connect-subscription/ConnectMessages.view';

import { useConnectSubscriptionFlow } from '../hooks/useConnectSubscriptionFlow';

import type { RootState } from '../types/state.types';

type RouteParams = { subId: string };

export const withConnectToSubscriptionMessages = (DoneComponent: React.ComponentType<any>) => {
  const ConnectedGuard: React.FC<{
    params: RouteParams;
  }> = ({ params, ...restProps }) => {
    const dispatch = useDispatch();

    const connecting = useSelector((state: RootState) => state.subscription.connecting);
    const connected = useSelector((state: RootState) => state.subscription.connected);
    const lastUrl = useSelector(
      (state: RootState) => state.subscription.lastConnectUrl || state.subscription.url
    );
    const persistedURLs = useSelector((state: RootState) => state.customConnectionURLs);

    const allChildComponentProps = { params, ...restProps };

    const connectToSubscriptionResults = useConnectSubscriptionFlow(
      `${location.origin}/api`,
      params.subId,
      connected
    );

    const nodeStatus = connectToSubscriptionResults?.nodeStatus;

    useLayoutEffect(() => {
      if (connectToSubscriptionResults?.nodeStatus === 'okay') {
        const customConnectionURL = persistedURLs[params.subId];

        dispatch(
          connectAttemptFinished({
            url: customConnectionURL ? customConnectionURL : connectToSubscriptionResults.url,
            token: connectToSubscriptionResults?.connectSubscriptionStatus?.jwtToken ?? '',
          })
        );
      }
    }, [
      connectToSubscriptionResults?.url,
      connectToSubscriptionResults?.connectSubscriptionStatus?.jwtToken,
    ]);

    if (!connecting && connected) {
      return <DoneComponent {...allChildComponentProps} />;
    }

    if (!nodeStatus) {
      return <ConnectMessages connectStatus="checking_connection" />;
    }

    if (nodeStatus === 'completed' || nodeStatus === 'trying_to_connect') {
      return <ConnectMessages connectStatus="trying_to_connect" />;
    }

    if (nodeStatus === 'poll_until_connect') {
      return <PollToConnectContainer />;
    }

    if (nodeStatus === 'okay') {
      return <DoneComponent {...allChildComponentProps} />;
    }

    if (nodeStatus) {
      return <ConnectMessages connectStatus={nodeStatus} lastUrl={lastUrl} />;
    }

    return <ConnectMessages connectStatus="checking_connection" />;
  };

  return ConnectedGuard;
};
